import React, { useState, useEffect } from "react";
import DOMPurify from "isomorphic-dompurify";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import {
  getPageBackground,
  getPrimaryText,
  getSecondaryText,
} from "../../utils/themeUtils";
import { getPostBySlug, Post } from "../../api/postsAPI";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import SEOHead from "../seo/SEOHead";
import ArticleSchema from "../seo/ArticleSchema";
import BreadcrumbSchema from "../seo/BreadcrumbSchema";
import { useSelector } from "react-redux";
import { selectSettings } from "../../store/slices/settingsSlice";
import PostComments from "./PostComments";
import PostReactions from "./PostReactions";

const ViewPostPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;

  const settings: any = useSelector(selectSettings);
  const [post, setPost] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      setIsLoading(true);
      const response = await getPostBySlug(slug);
      // A published post stays reachable by its link whatever the club's blog
      // switch says (it hides only the posts list), 2026-11-02.
      if (response.success && response.data?.id) {
        setPost(response.data);
      } else {
        setError(response.message || "Post not found");
      }
      setIsLoading(false);
    };
    fetchPost();
  }, [slug]);

  if (isLoading) {
    return (
      <div className={`flex flex-col min-h-screen ${getPageBackground(isDarkMode)}`}>
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className={`text-lg ${getPrimaryText(isDarkMode)}`}>Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className={`flex flex-col min-h-screen ${getPageBackground(isDarkMode)}`}>
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className={`text-2xl font-bold mb-2 ${getPrimaryText(isDarkMode)}`}>
              Post Not Found
            </h1>
            <p className={`mb-4 ${getSecondaryText(isDarkMode)}`}>{error || "This post does not exist."}</p>
            <button
              onClick={() => navigate(`/${tenantId}`)}
              className={`px-4 py-2 rounded-md transition-colors ${
                isDarkMode
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-800"
              }`}
            >
              Go Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const authorName = post.author ? `${post.author.first_name} ${post.author.last_name}` : undefined;

  return (
    <div className={`flex flex-col min-h-screen ${getPageBackground(isDarkMode)}`}>
      <SEOHead
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt || ""}
        ogType="article"
        ogImage={post.featured_image || undefined}
      />
      <BreadcrumbSchema items={[{ name: "Blog", path: "/posts" }, { name: post.title, path: `/posts/${slug}` }]} />
      <ArticleSchema
        headline={post.title}
        description={post.seo_description || post.excerpt}
        authorName={authorName}
        datePublished={post.published_at}
        dateModified={post.updated_at}
        image={post.featured_image || undefined}
        publisherName={settings?.business_name}
        publisherLogo={settings?.logo_url}
      />
      <Navbar />
      <article className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 sm:py-12">
        {/* Featured Image */}
        {post.featured_image && (
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-64 sm:h-80 object-cover rounded-lg mb-6"
          />
        )}

        {/* Categories */}
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.categories.map((category, idx) => (
              <span
                key={idx}
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {category}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${getPrimaryText(isDarkMode)}`}>
          {post.title}
        </h1>

        {/* Meta */}
        <div className={`flex items-center gap-3 mb-8 text-sm ${getSecondaryText(isDarkMode)}`}>
          {post.author && (
            <span>
              By {post.author.first_name} {post.author.last_name}
            </span>
          )}
          {post.published_at && (
            <>
              {post.author && <span>&middot;</span>}
              <span>{new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
            </>
          )}
        </div>

        {/* Content */}
        <div
          className={`prose max-w-none ${isDarkMode ? "prose-invert" : ""}`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || "") }}
        />

        {/* Reactions — gated by per-post + tenant-wide flag.
            comments_enabled / reactions_enabled per-post columns are
            null → inherit from settings. The page treats undefined as
            inherit, so we pass through both signals to the component
            and let the backend resolve. */}
        <PostReactions
          postId={post.id}
          enabled={
            (post as any).reactions_enabled === false
              ? false
              : settings?.allow_post_reactions !== false
          }
        />

        {/* Comments — same per-post + tenant-wide gating. */}
        <PostComments
          postId={post.id}
          enabled={
            (post as any).comments_enabled === false
              ? false
              : settings?.allow_post_comments !== false
          }
        />
      </article>
      <Footer />
    </div>
  );
};

export default ViewPostPage;
